import { cn } from '@freelancer-os/ui/src/lib/utils';
import { CreditCard, SlidersHorizontal, User, Users } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { BankTab } from './components/BankTab';
import { PreferencesTab } from './components/PreferencesTab';
import { ProfileTab } from './components/ProfileTab';
import { TeamTab } from './components/TeamTab';

const TAB_IDS = ['profile', 'team', 'bank', 'preferences'] as const;
const TAB_ICONS = { profile: User, team: Users, bank: CreditCard, preferences: SlidersHorizontal } as const;

type TabId = (typeof TAB_IDS)[number];

function isTabId(v: string | null): v is TabId {
  return v !== null && (TAB_IDS as readonly string[]).includes(v);
}

export default function SettingsPage() {
  const { t } = useTranslation('settings');
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<TabId>(isTabId(initialTab) ? initialTab : 'profile');

  return (
    <div className="max-w-6xl mx-auto py-8 px-6 w-full">
      <h1 className="text-2xl font-bold text-surface-900 tracking-tight mb-6">{t('title')}</h1>

      {/* Tab navigation */}
      <div className="flex gap-1 mb-8 border-b border-surface-200 w-full">
        {TAB_IDS.map((tabId) => {
          const isActive = activeTab === tabId;
          const Icon = TAB_ICONS[tabId];

          return (
            <button
              key={tabId}
              type="button"
              onClick={() => setActiveTab(tabId)}
              className={cn(
                'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
                isActive
                  ? 'border-brand-500 text-brand-700'
                  : 'border-transparent text-surface-500 hover:text-surface-700 hover:border-surface-300',
              )}
            >
              <Icon className="w-4 h-4" strokeWidth={1.8} />
              {t(`tabs.${tabId}`)}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'profile' && <ProfileTab />}
      {activeTab === 'team' && <TeamTab />}
      {activeTab === 'bank' && <BankTab />}
      {activeTab === 'preferences' && <PreferencesTab />}
    </div>
  );
}
